import * as React from "react";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { EmptyState } from "@/components/dashboard/EmptyState";
import { Stack } from "@mui/material";
import { useBrokerList } from "@/hooks/queries/useBrokerStatus";
import { useOrderEstimate } from "@/hooks/queries/useOrderPreview";
import type { InstrumentSummary } from "./InstrumentSearch";
import type { OrderTicketValues } from "./OrderTicket";

interface BrokerSummary {
  provider_id: string;
  name: string;
}

interface OrderEstimatePayload {
  estimated_value: number | null;
  estimated_brokerage: number | null;
  estimated_charges: number | null;
  estimated_margin: number | null;
}

interface OrderPreviewProps {
  instrument: InstrumentSummary | null;
  preview: OrderTicketValues | null;
}

/**
 * Backend v2.1 (Sprint 4.5) `POST /api/v1/orders/estimate` - reads
 * whatever order-economics values already exist for the Order Ticket's
 * own already-entered values, strictly read-only, never submitting an
 * order. `estimated_value` is the one genuinely computable field
 * (`quantity * price`, computed backend-side); `estimated_brokerage`/
 * `estimated_charges`/`estimated_margin` honestly render "Unavailable"
 * when the backend returns `null` (TD-103, no data source exists) -
 * never computed locally, which this sprint's STOP CONDITION disallows.
 */
export function OrderPreview({ instrument, preview }: OrderPreviewProps): React.JSX.Element {
  const brokersQuery = useBrokerList();
  const estimateQuery = useOrderEstimate(
    instrument && preview
      ? { instrumentToken: instrument.instrument_token, quantity: preview.quantity, price: preview.price }
      : null,
  );

  if (!instrument || !preview) {
    return (
      <StatusCard title="Order Preview">
        <EmptyState message="Fill in the Order Ticket and click Preview to see a summary here." />
      </StatusCard>
    );
  }

  return (
    <StatusCard title="Order Preview">
      <Stack spacing={1.5}>
        <MetricCard label="Selected Instrument" value={`${instrument.trading_symbol} (${instrument.exchange_code})`} />
        <MetricCard label="Requested Quantity" value={preview.quantity} />
        <QueryStateBoundary query={estimateQuery}>
          {(payload) => {
            const estimate = payload as unknown as OrderEstimatePayload;
            return (
              <>
                <MetricCard label="Estimated Value" value={estimate.estimated_value ?? "Unavailable"} />
                <MetricCard label="Estimated Brokerage" value={estimate.estimated_brokerage ?? "Unavailable"} />
                <MetricCard label="Estimated Charges" value={estimate.estimated_charges ?? "Unavailable"} />
                <MetricCard label="Estimated Margin" value={estimate.estimated_margin ?? "Unavailable"} />
              </>
            );
          }}
        </QueryStateBoundary>
        <QueryStateBoundary
          query={brokersQuery}
          isEmpty={(data) => (data as unknown as BrokerSummary[]).length === 0}
          emptyMessage="No broker providers are configured."
        >
          {(payload) => {
            const brokers = payload as unknown as BrokerSummary[];
            const names = brokers.map((b) => b.name).join(", ");
            return (
              <>
                <MetricCard label="Broker" value={names} />
                <MetricCard label="Provider" value={names} />
              </>
            );
          }}
        </QueryStateBoundary>
      </Stack>
    </StatusCard>
  );
}
